import React, { useCallback, useMemo, useState, useEffect } from 'react';
import {
  ScrollView,
  BackHandler,
  ActivityIndicator,
  Platform,
  View,
  StyleSheet,
  Pressable,
} from 'react-native';
import { Text, useThemeName } from '@tamagui/core';
import { YStack } from '@tamagui/stacks';
import { useNavigation } from '@react-navigation/native';
import { ChevronLeft } from 'lucide-react-native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type {
  RootStackParamList,
  RootStackScreenProps,
} from '@/types/navigation';
import { Screen } from '@/components';
import HeroImage from '../MeetingAnnouncementScreen/components/HeroImage';
import ProfileHeader from '../MeetingAnnouncementScreen/components/ProfileHeader';
import AboutSection from '../MeetingAnnouncementScreen/components/AboutSection';
import TagsSection from '../MeetingAnnouncementScreen/components/TagsSection';
import GallerySection from '../MeetingAnnouncementScreen/components/GallerySection';
import PhotoViewerModal from '../MeetingAnnouncementScreen/components/PhotoViewerModal';
import {
  SECTION_GAP,
  BOTTOM_PADDING,
} from '../MeetingAnnouncementScreen/constants';
import { getStyles } from '../MeetingAnnouncementScreen/styles';
import { usePublicProfile } from '@/api/hooks/useProfile';
import { useHobbies } from '@/api/hooks/useGendersAndHobbies';
import { COLORS } from '@/constants/colors';
import { useIsMobileLayout } from '@/hooks/useWindowWidth';

type Props = RootStackScreenProps<'UserProfile'>;

const UserProfileScreen = React.memo<Props>(({ route }) => {
  const isMobileLayout = useIsMobileLayout();
  const { userId } = route.params;
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const themeName = useThemeName();
  const isDark = themeName === 'dark';
  const styles = useMemo(() => getStyles(isDark), [isDark]);

  const [photoViewerVisible, setPhotoViewerVisible] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);

  // Fetch public profile of the user
  const {
    data: userPublicProfile,
    isLoading: isLoadingUserProfile,
    isError: isProfileError,
  } = usePublicProfile(userId, {
    enabled: !!userId,
  });

  // Fetch hobbies list for mapping hobbyIds to names
  const { data: hobbies = [] } = useHobbies();

  // Handle hardware back button on Android (only when photo viewer is visible)
  useEffect(() => {
    if (!photoViewerVisible) return;

    const backHandler = BackHandler.addEventListener(
      'hardwareBackPress',
      () => {
        setPhotoViewerVisible(false);
        return true;
      },
    );

    return () => backHandler.remove();
  }, [photoViewerVisible]);

  const heroImageUri = useMemo(() => {
    if (userPublicProfile?.photos && userPublicProfile.photos.length > 0) {
      const defaultPhoto =
        userPublicProfile.photos.find(p => p.isDefault) ||
        (userPublicProfile.defaultPhotoId
          ? userPublicProfile.photos.find(
              p => p.id === userPublicProfile.defaultPhotoId,
            )
          : null) ||
        userPublicProfile.photos[0];

      if (defaultPhoto) {
        return defaultPhoto.originalUrl;
      }
    }

    return 'https://static.photos/people/1200x630/0';
  }, [userPublicProfile]);

  // Gallery photos - return full UserPhotoView objects to preserve isLocked status
  const galleryPhotos = useMemo(() => {
    if (userPublicProfile?.photos && userPublicProfile.photos.length > 0) {
      const defaultPhoto =
        userPublicProfile.photos.find(p => p.isDefault) ||
        (userPublicProfile.defaultPhotoId
          ? userPublicProfile.photos.find(
              p => p.id === userPublicProfile.defaultPhotoId,
            )
          : null);

      // Get all photos except the default one (return full objects)
      const galleryPhotoObjects = userPublicProfile.photos.filter(p =>
        defaultPhoto ? p.id !== defaultPhoto.id : true,
      );

      if (galleryPhotoObjects.length > 0) {
        return galleryPhotoObjects;
      }

      // If only default photo exists, show it in gallery too
      if (userPublicProfile.photos.length > 0) {
        return userPublicProfile.photos;
      }
    }

    // Fallback to placeholder photos
    return [
      'https://static.photos/people/1200x630/3',
      'https://static.photos/people/1200x630/0',
      'https://static.photos/people/1200x630/2',
    ];
  }, [userPublicProfile]);

  // Gallery photo URLs for PhotoViewerModal
  const galleryPhotoUrls = useMemo(() => {
    return galleryPhotos.map(photo =>
      typeof photo === 'string' ? photo : photo.originalUrl,
    );
  }, [galleryPhotos]);

  // Generate tags from user profile hobbies
  const tags = useMemo(() => {
    if (!userPublicProfile?.hobbyIds || !hobbies.length) return [];

    const hobbyMap = new Map(hobbies.map(h => [h.id, h.name]));

    return userPublicProfile.hobbyIds
      .map(hobbyId => {
        const hobbyName = hobbyMap.get(Number(hobbyId));
        if (hobbyName) {
          return { text: hobbyName, checked: true };
        }
        return null;
      })
      .filter((tag): tag is { text: string; checked: boolean } => tag !== null);
  }, [userPublicProfile?.hobbyIds, hobbies]);

  const handlePhotoPress = useCallback((index: number) => {
    setSelectedPhotoIndex(index);
    setPhotoViewerVisible(true);
  }, []);

  const handleClosePhotoViewer = useCallback(() => {
    setPhotoViewerVisible(false);
  }, []);

  const handleUnlockPress = useCallback(() => {
    // TODO: Implement unlock gallery via API
  }, []);

  const handleBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  // Loading state
  if (isLoadingUserProfile) {
    return (
      <Screen edges={['top', 'bottom', 'left', 'right']}>
        <YStack flex={1} alignItems="center" justifyContent="center">
          <ActivityIndicator size="large" color={COLORS.PRIMARY} />
          <Text fontSize={14} color="$color" opacity={0.7} marginTop="$3">
            Ładowanie profilu...
          </Text>
        </YStack>
      </Screen>
    );
  }

  // Error state
  if (isProfileError || !userPublicProfile) {
    return (
      <Screen edges={['top', 'bottom', 'left', 'right']}>
        <YStack flex={1} alignItems="center" justifyContent="center">
          <Text fontSize={16} color="$color" opacity={0.7}>
            Nie udało się pobrać profilu
          </Text>
          <Pressable onPress={handleBack} style={{ marginTop: 16 }}>
            <Text fontSize={14} color={COLORS.PRIMARY}>
              Wróć
            </Text>
          </Pressable>
        </YStack>
      </Screen>
    );
  }

  const displayName = userPublicProfile.nickName || 'Anonim';
  const age = userPublicProfile.age || 0;
  const isVerified = userPublicProfile.isVerified || false;
  const bio = userPublicProfile.bio || 'Brak opisu';
  const rating =
    userPublicProfile.ratingAverage > 0
      ? userPublicProfile.ratingAverage.toFixed(1)
      : 'Brak oceny';

  // Web/Tablet layout - two columns
  if (Platform.OS === 'web' && !isMobileLayout) {
    return (
      <Screen edges={['top', 'bottom', 'left', 'right']}>
        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={webStyles.scrollContent}
        >
          {/* Back button and title */}
          <View style={webStyles.header}>
            <Pressable onPress={handleBack} style={webStyles.backButton}>
              <ChevronLeft size={24} color={isDark ? '#ffffff' : '#18181b'} />
            </Pressable>
            <Text
              fontSize={20}
              fontWeight="600"
              color={isDark ? '#ffffff' : '#18181b'}
            >
              Profil użytkownika
            </Text>
          </View>

          {/* Top row: Photo + Info side by side */}
          <View style={webStyles.topRow}>
            {/* Left column - Photo */}
            <View style={webStyles.leftColumn}>
              <View
                style={[
                  webStyles.photoContainer,
                  {
                    backgroundColor: isDark ? COLORS.ZINC_900 : COLORS.ZINC_200,
                  },
                ]}
              >
                <HeroImage imageUri={heroImageUri} />
              </View>
            </View>

            {/* Right column - Profile info, About */}
            <View style={webStyles.rightColumn}>
              <View
                style={[
                  webStyles.infoCard,
                  { backgroundColor: isDark ? COLORS.ZINC_900 : '#ffffff' },
                ]}
              >
                <ProfileHeader
                  name={displayName}
                  age={age}
                  verified={isVerified}
                  tagline={`${rating} - ${
                    userPublicProfile.ratingCount || 0
                  } opinii`}
                  isDark={isDark}
                  photoUrl={heroImageUri}
                />
              </View>

              <View
                style={[
                  webStyles.infoCard,
                  { backgroundColor: isDark ? COLORS.ZINC_900 : '#ffffff' },
                ]}
              >
                <AboutSection about={bio} isDark={isDark} />
              </View>
            </View>
          </View>

          {/* Bottom row: Tags, Gallery - full width */}
          <View style={webStyles.bottomRow}>
            {tags.length > 0 && (
              <View
                style={[
                  webStyles.infoCard,
                  { backgroundColor: isDark ? COLORS.ZINC_900 : '#ffffff' },
                ]}
              >
                <TagsSection tags={tags} isDark={isDark} />
              </View>
            )}

            <View
              style={[
                webStyles.infoCard,
                { backgroundColor: isDark ? COLORS.ZINC_900 : '#ffffff' },
              ]}
            >
              <GallerySection
                photos={galleryPhotos}
                onPhotoPress={handlePhotoPress}
                onUnlockPress={handleUnlockPress}
                isDark={isDark}
              />
            </View>
          </View>
        </ScrollView>

        <PhotoViewerModal
          visible={photoViewerVisible}
          imageUri={galleryPhotoUrls[selectedPhotoIndex]}
          onClose={handleClosePhotoViewer}
        />
      </Screen>
    );
  }

  // Mobile layout - single column
  return (
    <Screen edges={['top', 'bottom', 'left', 'right']}>
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={[
          styles.scrollContent,
          { paddingBottom: BOTTOM_PADDING },
        ]}
        style={styles.scrollView}
      >
        <YStack style={styles.container}>
          {/* Hero Image */}
          <YStack>
            <HeroImage imageUri={heroImageUri} />
          </YStack>

          {/* White Card with Profile Info */}
          <YStack style={styles.contentCard}>
            <YStack gap={SECTION_GAP}>
              <ProfileHeader
                name={displayName}
                age={age}
                verified={isVerified}
                tagline={`${rating} - ${
                  userPublicProfile.ratingCount || 0
                } opinii`}
                isDark={isDark}
                photoUrl={heroImageUri}
              />

              <AboutSection about={bio} isDark={isDark} />

              {tags.length > 0 && <TagsSection tags={tags} isDark={isDark} />}

              <GallerySection
                photos={galleryPhotos}
                onPhotoPress={handlePhotoPress}
                onUnlockPress={handleUnlockPress}
                isDark={isDark}
              />
            </YStack>
          </YStack>
        </YStack>
      </ScrollView>

      <PhotoViewerModal
        visible={photoViewerVisible}
        imageUri={galleryPhotoUrls[selectedPhotoIndex]}
        onClose={handleClosePhotoViewer}
      />
    </Screen>
  );
});

const webStyles = StyleSheet.create({
  scrollContent: {
    padding: 24,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    gap: 12,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(0,0,0,0.05)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  topRow: {
    flexDirection: 'row',
    gap: 16,
  },
  bottomRow: {
    marginTop: 60,
    gap: 12,
  },
  leftColumn: {
    width: '40%',
    minWidth: 200,
  },
  rightColumn: {
    flex: 1,
    gap: 12,
  },
  photoContainer: {
    borderRadius: 16,
    overflow: 'hidden',
    aspectRatio: 3 / 4,
  },
  infoCard: {
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.08)',
  },
});

UserProfileScreen.displayName = 'UserProfileScreen';

export default UserProfileScreen;
